'use client';
import { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import Image from 'next/image';
import { Play, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { showcaseVideos } from './constants';
import { useLanguage } from '@/components/LanguageProvider';

export function CinematicVideoTransition({ activeVideo, setActiveVideo }: any) {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start start", "end start"] });
  
  const videoOpacity = useTransform(scrollYProgress, [0.05, 0.25, 0.55, 0.65], [0, 1, 1, 0]);
  const videoScale = useTransform(scrollYProgress, [0.05, 0.25, 0.5, 0.65], [1.15, 1, 1, 0]);
  const videoRadius = useTransform(scrollYProgress, [0.5, 0.65], [0, 100]); 
  const overlayOpacity = useTransform(scrollYProgress, [0.5, 0.6], [0.3, 0.7]);
  const textOpacity = useTransform(scrollYProgress, [0.3, 0.4, 0.5], [0, 1, 0]);
  const textY = useTransform(scrollYProgress, [0.3, 0.5], [50, -50]);
  const progressHeight = useTransform(scrollYProgress, [0.25, 0.5], ["0%", "100%"]);
  const progressOpacity = useTransform(scrollYProgress, [0.2, 0.25, 0.5, 0.55], [0, 1, 1, 0]);
  const marqueeOpacity = useTransform(scrollYProgress, [0.55, 0.65], [0, 1]);
  const marqueeScale = useTransform(scrollYProgress, [0.55, 0.65], [0.95, 1]);

  const { t } = useLanguage();
  const tv = t.landing.video;

  return (
    <section ref={containerRef} className="relative h-[300vh] md:h-[400vh] w-full bg-transparent z-20 -mt-[100vh]">
      <div className="sticky top-0 w-full h-screen overflow-hidden flex items-center justify-center pointer-events-none">
        <motion.div style={{ opacity: videoOpacity }} className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none transform-gpu">
            <motion.div style={{ scale: videoScale, borderRadius: videoRadius }} className="w-full h-full md:w-[100vw] md:h-[100vh] origin-center overflow-hidden will-change-transform bg-black">
            <video autoPlay loop muted playsInline className="w-full h-full object-cover transform-gpu">
                <source src="/landing2.0/animation.mp4" type="video/mp4" />
            </video> 
            <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black transform-gpu" />
            </motion.div>
        </motion.div>
        
        <motion.div style={{ opacity: textOpacity, y: textY }} className="absolute z-20 px-4 md:px-8 w-full text-center transform-gpu">
          <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-extrabold uppercase text-white leading-tight">
            {tv.title1} <br />
            <span className="text-[#667fff]">{tv.title2}</span>
          </h2>
        </motion.div>
        
        <motion.div style={{ opacity: progressOpacity }} className="absolute z-20 left-2 md:left-12 top-1/2 -translate-y-1/2 w-1.5 h-40 md:h-64 bg-white/10 rounded-full overflow-hidden flex flex-col justify-start transform-gpu">
          <motion.div style={{ height: progressHeight }} className="w-full bg-[#667fff] rounded-full" />
        </motion.div>
        
        <motion.div style={{ opacity: marqueeOpacity, scale: marqueeScale }} className="absolute inset-0 z-30 flex items-center justify-center will-change-transform bg-transparent transform-gpu">
           <VideoMarqueeSection activeVideo={activeVideo} setActiveVideo={setActiveVideo} tv={tv} />
        </motion.div>
      </div>
    </section>
  );
}

function VideoMarqueeSection({ activeVideo, setActiveVideo, tv }: any) {
  const trackItems = [...showcaseVideos, ...showcaseVideos, ...showcaseVideos, ...showcaseVideos];
  const isPlaying = activeVideo === null;

  return (
    <div className={`marquee-section group/marquee relative w-full h-full flex flex-col justify-center items-center gap-10 md:gap-14 overflow-hidden bg-transparent pointer-events-none ${!isPlaying ? 'is-paused' : ''}`}>
      <div className="w-full text-center px-4 z-10 pointer-events-none flex flex-col items-center">
        <h2 className="font-mc text-3xl md:text-5xl uppercase text-white">
          {tv.marquee1} <span className="text-[#667fff]">{tv.marquee2}</span>
        </h2>
        <p className="text-[#aaaaaa] mt-2 text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase opacity-60">KIZRUM💡Production</p>
      </div>
      <motion.div style={{ rotate: -3, scale: 1.05 }} className="w-full flex flex-col gap-4 md:gap-8 relative z-20 transform-gpu">
        <div className="pause-on-hover flex w-max pointer-events-none">
          <div className="flex gap-4 md:gap-6 px-2 md:px-3 animate-marquee-left transform-gpu">
            {trackItems.map((video, idx) => (
              <VideoCard key={`r1-${idx}`} video={video} onClick={() => setActiveVideo({ ...video, sourceRow: 1, sourceIdx: idx })} />
            ))}
          </div>
        </div>
        <div className="pause-on-hover flex w-max pointer-events-none -ml-[30%] md:-ml-[50%]">
          <div className="flex gap-4 md:gap-6 px-2 md:px-3 animate-marquee-right transform-gpu">
            {trackItems.map((video, idx) => (
              <VideoCard key={`r2-${idx}`} video={video} onClick={() => setActiveVideo({ ...video, sourceRow: 2, sourceIdx: idx })} />
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function VideoCard({ video, onClick }: { video: any, onClick: () => void }) {
  return (
    <motion.div initial={{ scale: 0.8, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} whileHover={{ scale: 1.05, zIndex: 20 }} viewport={{ once: true, margin: "100px" }} transition={{ duration: 0.4 }} className="relative w-[260px] sm:w-[280px] md:w-[400px] aspect-video shrink-0 overflow-hidden rounded-[16px] md:rounded-[20px] cursor-pointer border border-white/10 bg-[#161616] pointer-events-auto transition-[opacity] duration-500 group-hover/marquee:opacity-40 hover:!opacity-100 group/card" onClick={onClick}>
      <Image src={video.thumb} alt={video.title} fill sizes="(max-width: 768px) 300px, 400px" loading="lazy" className="object-cover opacity-80 group-hover/card:opacity-100 transition-all duration-700 cursor-pointer" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover/card:opacity-40 transition-opacity duration-300" />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white scale-90 opacity-0 group-hover/card:scale-100 group-hover/card:opacity-100 transition-all duration-300">
          <Play size={20} fill="currentColor" className="ml-1 md:w-6 md:h-6" />
        </div>
      </div>
      <div className="absolute bottom-3 left-4 right-4 md:bottom-4 md:left-5 md:right-5 translate-y-2 opacity-0 group-hover/card:translate-y-0 group-hover/card:opacity-100 transition-all duration-300 pointer-events-none">
        <h4 className="text-white font-bold text-xs sm:text-sm md:text-base uppercase tracking-wider truncate">{video.title}</h4>
      </div>
    </motion.div>
  );
}

export function VideoModal({ video, onClose, onNext, onPrev }: { video: any, onClose: () => void, onNext: () => void, onPrev: () => void }) {
  const [showPlayer, setShowPlayer] = useState(false);
  const { t } = useLanguage();
  const tv = t.landing.video;

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') { setShowPlayer(false); onClose(); } };
    window.addEventListener('keydown', handleEsc);
    const timer = setTimeout(() => setShowPlayer(true), 400); 
    return () => { window.removeEventListener('keydown', handleEsc); clearTimeout(timer); };
  }, [video, onClose]);

  return (
    <div className="fixed inset-0 z-[200] flex flex-col items-center justify-center pointer-events-auto" onWheel={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}>
      <style dangerouslySetInnerHTML={{ __html: `html, body { overflow: hidden !important; overscroll-behavior: none !important; }`}} />
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="absolute inset-0 bg-[#111111]/95 backdrop-blur-sm cursor-pointer" onClick={() => { setShowPlayer(false); onClose(); }} />
      <motion.div initial={{ y: -50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -50, opacity: 0 }} transition={{ duration: 0.3 }} className="absolute top-0 left-0 w-full p-4 md:p-10 flex justify-between items-start z-50 bg-gradient-to-b from-black/80 to-transparent pointer-events-none">
        <div>
          <p className="text-[#667fff] text-[10px] md:text-sm font-bold tracking-[0.2em] uppercase mb-1">{tv.modalTitle}</p>
          <h3 className="text-white text-xl md:text-4xl font-black uppercase tracking-tighter">{video.title}</h3>
        </div>
        <button onClick={() => { setShowPlayer(false); onClose(); }} className="w-10 h-10 md:w-12 md:h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors cursor-pointer pointer-events-auto shrink-0">
          <X size={20} className="md:w-6 md:h-6"/>
        </button>
      </motion.div>
      <div className="w-full max-w-[1400px] px-2 md:px-16 flex items-center justify-center relative pointer-events-none mt-10 md:mt-0 z-10">
        <motion.button initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} onClick={(e) => { e.stopPropagation(); setShowPlayer(false); onPrev(); }} className="absolute -bottom-16 left-1/4 md:bottom-auto md:top-1/2 md:-translate-y-1/2 md:left-4 z-50 w-12 h-12 md:w-16 md:h-16 rounded-full bg-black/70 hover:bg-[#667fff] border border-white/20 flex items-center justify-center text-white transition-all pointer-events-auto cursor-pointer">
            <ChevronLeft size={24} className="md:w-8 md:h-8" />
        </motion.button>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.3, ease: "easeOut" }} onClick={(e) => e.stopPropagation()} className="relative w-full max-w-5xl aspect-video bg-black rounded-xl md:rounded-[24px] overflow-hidden border border-white/10 pointer-events-auto mx-2 md:mx-12 cursor-default">
          <Image src={video.thumb} fill className="object-cover opacity-60 pointer-events-none" alt="" />
          {showPlayer && (
            <motion.iframe initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1&rel=0&showinfo=0`} allow="autoplay; encrypted-media; fullscreen" className="absolute inset-0 w-full h-full border-0 z-10" />
          )}
        </motion.div>
        <motion.button initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} onClick={(e) => { e.stopPropagation(); setShowPlayer(false); onNext(); }} className="absolute -bottom-16 right-1/4 md:bottom-auto md:top-1/2 md:-translate-y-1/2 md:right-4 z-50 w-12 h-12 md:w-16 md:h-16 rounded-full bg-black/70 hover:bg-[#667fff] border border-white/20 flex items-center justify-center text-white transition-all pointer-events-auto cursor-pointer">
            <ChevronRight size={24} className="md:w-8 md:h-8" />
        </motion.button>
      </div>
    </div>
  );
}